import { action, makeObservable, observable, onBecomeObserved, runInAction } from "mobx";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { AppStore } from "@/store";
import { ChromeExtensionIntegrationOrganizingInstructions, IChromeExtensionStore } from "./types";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { debounce } from "lodash-es";
import { asyncDataLoaded, asyncDataLoading } from "@/domains/async/AsyncData";
import { clientEnvModule } from "@/modules/client-env";

export class AppStoreChromeExtensionStore implements IChromeExtensionStore {
  isInstalled = asyncDataLoading<boolean>({});
  isLoading = false;
  isSaving = false;
  organizingInstructions: ChromeExtensionIntegrationOrganizingInstructions = null;
  organizingInstructionsEnabled: boolean = false;

  constructor(private readonly store: AppStore) {
    makeObservable<AppStoreChromeExtensionStore>(this, {
      // Page lifecycle
      isInstalled: observable,
      useIsChromeExtensionInstalledUpdater: false,

      // Settings
      organizingInstructionsEnabled: observable,
      organizingInstructions: observable,

      // Loading / Saving
      load: action,
      isLoading: observable,
      save: action,
      isSaving: observable,
    });

    onBecomeObserved(this, "organizingInstructions", () => {
      this.load();
    });
  }

  load = async () => {
    runInAction(() => {
      this.isLoading = true;
    });

    try {
      const response = await api.get("/v2/integrations/web-clips/settings", {});

      runInAction(() => {
        this.organizingInstructionsEnabled = response.data?.instructions_enabled ?? false;
        this.organizingInstructions = response.data?.instructions_markdown_content ?? null;
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "Failed to load web clip settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  save = async ({
    organizingInstructions,
    organizingInstructionsEnabled,
  }: {
    organizingInstructions: ChromeExtensionIntegrationOrganizingInstructions;
    organizingInstructionsEnabled: boolean;
  }) => {
    try {
      runInAction(() => {
        this.isSaving = true;
      });

      const enabled = organizingInstructionsEnabled ?? false;
      const instructions = organizingInstructions ?? null;

      const response = await api.post("/v2/integrations/web-clips/settings", {
        body: {
          instructions_enabled: enabled,
          instructions_markdown_content: instructions,
        },
      });

      const data = response.data;
      if (!data || response.error) {
        const error = response.error as { detail: string };
        throw new Error(error.detail ?? "Failed to save email settings");
      }

      runInAction(() => {
        // Save altogther to avoid ui flickering.
        this.isSaving = false;
        this.organizingInstructionsEnabled = enabled;
        this.organizingInstructions = instructions;
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreChromeExtensionStore] Failed to save web clip settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: "Could not save organizing instructions",
      });
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  useIsChromeExtensionInstalledUpdater = () => {
    useEffectOnMount(() => {
      if (clientEnvModule.isDesktop()) {
        runInAction(() => {
          this.isInstalled = asyncDataLoaded(this.isInstalled, false);
        });
        return;
      }
      const f = debounce(
        () => {
          const isInstalled = document.querySelector("#mem-ext-shadow-host") !== null;
          runInAction(() => {
            this.isInstalled = asyncDataLoaded(this.isInstalled, isInstalled);
          });
        },
        200,
        { leading: false, trailing: true }
      );
      f();
      document.addEventListener("change", f);
      return () => {
        document.removeEventListener("change", f);
      };
    });
  };
}
